// How often a person runs a Method, from the start times of its runs in the last 30 days, in the viewer's time zone.
// The Methods panel shows it, and a Method is due when its usual gap has passed.

export type Rhythm = { label: string; detail: string | null; due: boolean };

const DAY = 86_400_000;
const dayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const short = (date: Date) => date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function rhythm(runTimes: string[], now = new Date()): Rhythm | null {
  if (!runTimes.length) return null;
  const days = [...new Map(runTimes.map(time => { const date = startOfDay(new Date(time)); return [dayKey(date), date] as const; })).values()]
    .sort((a, b) => a.getTime() - b.getTime());
  const last = days.at(-1)!;
  if (days.length === 1) {
    const today = dayKey(last) === dayKey(now);
    return { label: today ? "Only today" : `Only ${short(last)}`, detail: runTimes.length > 1 ? `${runTimes.length} runs` : null, due: false };
  }
  const gaps = days.slice(1).map((day, index) => Math.round((day.getTime() - days[index]!.getTime()) / DAY)).sort((a, b) => a - b);
  const gap = gaps[Math.floor(gaps.length / 2)]!;
  const label = gap <= 1 ? "Every day" : gap >= 6 && gap <= 8 ? "Every week" : gap > 14 ? `On ${days.length} days in 30` : `About every ${gap} days`;
  // Due only for a regular rhythm: 3 or more days with a run and a gap of a week or less.
  if (days.length < 3 || gap > 7) return { label, detail: null, due: false };
  const next = new Date(last.getTime() + gap * DAY);
  if (startOfDay(now).getTime() > next.getTime()) return { label, detail: `Due · last run ${short(last)}`, due: true };
  if (dayKey(next) === dayKey(now) || startOfDay(now).getTime() >= next.getTime()) return { label, detail: "Next today", due: false };
  return { label, detail: `Next about ${short(next)}`, due: false };
}
